import React from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { Operadora } from '../../types';
import { operadoraFormFields } from '../../config/formFields';

interface OperadorasSectionProps {
  data: {
    operadoras: Operadora[];
  };
  operations: {
    handleOperadoraSubmit: (data: Record<string, any>) => Promise<void>;
    handleOperadoraToggle: (item: Operadora) => Promise<void>;
    handleOperadoraDelete: (item: Operadora) => Promise<void>;
    handleOperadoraEdit: (item: Operadora, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const OperadorasSection: React.FC<OperadorasSectionProps> = ({ data, operations, userPermissions }) => {
  // Table column configurations
  const operadoraColumns: TableColumn<Operadora>[] = [
    {
      key: 'nombre',
      title: 'Nombre de la Operadora',
      width: '35%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleOperadoraEdit
    },
    {
      key: 'color',
      title: 'Color Corporativo',
      width: '20%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleOperadoraEdit,
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '20px',
            height: '20px',
            backgroundColor: value || '#ccc',
            border: '1px solid #999',
            borderRadius: '4px'
          }} />
          <span>{value || '(Sin color)'}</span>
        </div>
      )
    },
    {
      key: 'empresa',
      title: 'Empresa',
      width: '20%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleOperadoraEdit,
      render: (value) => <span>{value || '(Sin empresa)'}</span>
    }
  ];

  // Quick Setup Handler for standard operators
  const handleOperadorasQuickSetup = async () => {
    if (window.confirm('¿Cargar operadoras estándar de Argentina? Esta acción agregará múltiples registros.')) {
      const standardOperadoras = [
        { nombre: 'YPF S.A.', color: '#0065BD' },
        { nombre: 'Shell Argentina S.A.', color: '#FFCC00' },
        { nombre: 'Total Austral S.A.', color: '#EE3124' },
        { nombre: 'Pan American Energy LLC', color: '#003DA5' },
        { nombre: 'Tecpetrol S.A.', color: '#56B046' },
        { nombre: 'Vista Oil & Gas', color: '#FF6600' },
        { nombre: 'Pluspetrol S.A.', color: '#009639' },
        { nombre: 'Wintershall Dea', color: '#E2001A' },
        { nombre: 'ExxonMobil', color: '#FF0000' },
        { nombre: 'Chevron Argentina S.R.L.', color: '#003478' }
      ];

      try {
        for (const operadora of standardOperadoras) {
          await operations.handleOperadoraSubmit({
            ...operadora,
            habilitada: true
          });
        }
        alert('✅ Operadoras estándar cargadas exitosamente');
      } catch (error) {
        console.error('Error cargando operadoras:', error);
        alert('❌ Error cargando operadoras. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>🏢 Gestión de Operadoras</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Administra las compañías operadoras que contratan los servicios de control de sólidos
        </p>
      </div>

      {/* Operadoras Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Operadoras',
          '🏢',
          data.operadoras,
          operadoraFormFields,
          operadoraColumns,
          operations.handleOperadoraSubmit,
          operations.handleOperadoraToggle,
          operations.handleOperadoraDelete,
          {
            description: 'Gestiona las compañías petroleras y operadoras que requieren servicios de control de sólidos',
            quickSetup: {
              title: 'Carga Rápida de Operadoras',
              description: 'Carga operadoras principales del mercado argentino (YPF, Shell, Total, PAE, etc.)',
              buttonLabel: '🏢 Cargar Operadoras Estándar',
              onQuickSetup: handleOperadorasQuickSetup
            }
          }
        )}
      />

      {/* Summary Section */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#e8f5e8',
        borderRadius: '12px',
        border: '2px solid #4CAF50'
      }}>
        <h3 style={{ color: '#2E7D32', marginBottom: '15px' }}>📊 Resumen de Operadoras</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginTop: '15px'
        }}>
          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🏢 Total Operadoras
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              {data.operadoras.length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ✅ Activas
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              {data.operadoras.filter(op => op.habilitada).length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ❌ Inactivas
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#f44336'
            }}>
              {data.operadoras.filter(op => !op.habilitada).length}
            </div>
          </div>
        </div>

        {data.operadoras.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No hay operadoras registradas. Usa la carga rápida para comenzar.
          </div>
        )}
      </div>
    </>
  );
};